/*
All parameters should be in snake_case
*/

// The payload types for every name `@heidi/session/contributions` declares now
// live in `@heidi/session/contracts`; they are re-exported below at STATEMENT
// level so importers and `jest.mock` literals keep resolving. What stayed is
// what session does not own outright — see the openspec 2.1c note for the
// per-name emitter measurement — plus the copy vocabulary, which is app-only.

import { type CopySettingsWire } from '@/features/copySettings/useCopySettings.types';
import { type NoteEditorType } from '@/models/analytics/features/notes';
import { type SubscriptionTier } from '@heidi/schema/ml-scribe';
import {
  ActiveSessionControlAction,
  IndicatorDisplayMode,
  SessionCreatedEntryPoint,
} from '@heidi/session/contracts';

import type {
  ActiveSessionControlClickedEventProps,
  ActiveSessionIndicatorClickedEventProps,
  ActiveSessionNavigationEventProps,
  CopyMethod,
  OutputSourceType,
  PersonalizationUpdatedAction,
  PersonalizationUpdatedEventProps,
  ScheduleSyncedEventProps,
  ScheduleUploadCompletedEventProps,
  ScheduleUploadEntryPoint,
  ScheduleUploadMethod,
  ScheduleUploadProcessingFailedEventProps,
  ScheduleUploadPromoClickedEventProps,
  ScheduleUploadPromoViewedEventProps,
  ScheduleUploadSessionsCreatedEventProps,
  ScheduleUploadStartedEventProps,
  ScheduleViewedEventProps,
  SessionCopiedFormat,
  SessionCopySource,
  SessionCreatedEventProps,
  SessionDeletedAction,
  SessionDeletedEventProps,
  SessionDetailResumeRecordingDevice,
  SessionDetailResumeStartedEventProps,
  SessionFilterType,
  SessionListVersion,
  SessionMarkedAsContextOnlyEventProps,
  SessionSearchUsageBaselineEventProps,
  SessionTabExitReason,
  SessionTabSwitchedEventProps,
  SessionTabViewedEventProps,
  SessionUpdatedAction,
  SessionUpdatedEventProps,
  SessionsFilterAppliedEventProps,
  SessionsListPaginatedEventProps,
  SessionsScopeChangedEventProps,
  SessionsSortChangedEventProps,
  TranscriptDeletedEventProps,
} from '@heidi/session/contracts';

export { ActiveSessionControlAction, IndicatorDisplayMode, SessionCreatedEntryPoint };
export type {
  ActiveSessionControlClickedEventProps,
  ActiveSessionIndicatorClickedEventProps,
  ActiveSessionNavigationEventProps,
  OutputSourceType,
  PersonalizationUpdatedAction,
  PersonalizationUpdatedEventProps,
  ScheduleSyncedEventProps,
  ScheduleUploadCompletedEventProps,
  ScheduleUploadEntryPoint,
  ScheduleUploadMethod,
  ScheduleUploadProcessingFailedEventProps,
  ScheduleUploadPromoClickedEventProps,
  ScheduleUploadPromoViewedEventProps,
  ScheduleUploadSessionsCreatedEventProps,
  ScheduleUploadStartedEventProps,
  ScheduleViewedEventProps,
  SessionCreatedEventProps,
  SessionDeletedAction,
  SessionDeletedEventProps,
  SessionDetailResumeRecordingDevice,
  SessionDetailResumeStartedEventProps,
  SessionFilterType,
  SessionListVersion,
  SessionMarkedAsContextOnlyEventProps,
  SessionSearchUsageBaselineEventProps,
  SessionTabExitReason,
  SessionTabSwitchedEventProps,
  SessionTabViewedEventProps,
  SessionUpdatedAction,
  SessionUpdatedEventProps,
  SessionsFilterAppliedEventProps,
  SessionsListPaginatedEventProps,
  SessionsScopeChangedEventProps,
  SessionsSortChangedEventProps,
  TranscriptDeletedEventProps,
};

export enum SessionCopiedType {
  NOTE = 'note',
  CONTEXT = 'context',
  TRANSCRIPTION = 'transcription',
}

/**
 * Snapshot of all four copy-formatting toggles. Derived from the feature's
 * snake_case wire shape so a new toggle propagates here automatically rather
 * than silently diverging (the two were previously identical by hand).
 */
export type CopySettingsSnapshot = CopySettingsWire;

/** One of the copy-formatting toggles, in snake_case wire form. */
export type CopySetting = keyof CopySettingsSnapshot;

export type SessionCopiedEventProps = {
  /**
   * Session identifier. Resolved client-side from the active /scribe/session
   * route; undefined when copied from non-session surfaces (e.g. shared /
   * read-only views).
   */
  session_id?: string;
  format: SessionCopiedFormat;
  copy_method: CopyMethod;
  /** Number of words in the copied selection */
  selected_word_count: number;
  /** Total number of words in the text editor */
  total_word_count: number;
  /** Percentage of content copied (selected_word_count / total_word_count * 100) */
  percentage_copied: number;
  /**
   * Toggle state at copy time. Attached on every format, not just
   * `with_toggles`, so a legacy-cohort copy stays comparable with a
   * toggle-cohort copy across a flag flip. Omitted only by the verbatim
   * clipboard paths, which never consult the toggles.
   */
  copy_settings?: CopySettingsSnapshot;
  /** Which affordance the copy came from. */
  copy_source: SessionCopySource;
  /**
   * Whether a `text/html` flavor accompanied `text/plain` on the clipboard.
   * `format` alone under-determines this: a `with_toggles` copy with every
   * toggle off writes plain text only.
   */
  html_flavor_written: boolean;
  /**
   * Which formatting this copy removed, from the shared formatting-loss
   * detector (`@heidi/editor/core` `detectCopyFormattingLoss`). A key is true
   * only when the copied content had that formatting and the copy removed it.
   * All four keys or omitted, never partial: omitted wherever the detector did
   * not run (verbatim clipboard paths, legacy non-toggle paths). Booleans only,
   * never copied content. `session_copied` 3-0-7 (SCR-7863).
   */
  formatting_removed?: {
    headings: boolean;
    bold_and_italics: boolean;
    bullet_points: boolean;
    tables: boolean;
  };
  /**
   * Editor variant the copy happened in. Uses the `NoteEditorType` vocabulary
   * (vMarkdown ⇒ `block_markdown`), which deliberately differs from the
   * `section_*` events' `SectionEditorType`, where that editor is `vmarkdown`.
   */
  editor_type?: NoteEditorType;
};

/** Fired when the user flips a copy-settings toggle. */
export type CopySettingsUpdatedEventProps = {
  /** Which toggle the user changed. */
  setting: CopySetting;
  /** The value the user changed it to. */
  is_enabled: boolean;
  /** Full toggle state after the change. */
  copy_settings: CopySettingsSnapshot;
};

export type SessionViewedEventProps = {
  session_id: string;
  /** User's current billing plan at the time of the event. */
  current_plan?: SubscriptionTier;
};
